import { ColDef, ITooltipParams, ValueFormatterParams } from '@ag-grid-community/core';
import { inject, Injectable } from '@angular/core';
import type { GetIemAssetsQuery } from '@uigy/generated/graphql';
import { Asset, AssetField, Criticality, LeanAsset, TopRiskAssetsGQL } from '@uigy/generated/graphql';
import { IconsEnum } from '@uigy/generated/icons.enum';
import { InventoryDataHelperService } from '@uigy/modules/inventory/services/inventory-data-helper.service';
import { InventoryUiGridService } from '@uigy/modules/inventory/services/inventory-ui-grid.service';
import { RiskScoreComponent } from '@uigy/shared/components/risk-widget/risk-score/risk-score.component';
import {
  AssetTypeCellRendererComponent,
} from '@uigy/shared/components/ui-grid/components/cell-renderers/asset-type-cell-renderer/asset-type-cell-renderer.component';
import {
  CriticalityCellRendererComponent,
} from '@uigy/shared/components/ui-grid/components/cell-renderers/criticality-cell-renderer/criticality-cell-renderer.component';
import {
  RiskScoreCellRendererComponent,
} from '@uigy/shared/components/ui-grid/components/cell-renderers/risk-score-cell-renderer/risk-score-cell-renderer.component';
import {
  VendorRendererComponent,
} from '@uigy/shared/components/ui-grid/components/cell-renderers/vendor-renderer/vendor-renderer.component';
import { UiGridIdsEnum } from '@uigy/shared/components/ui-grid/enums/ui-grid-ids.enum';
import { UiGridOptions } from '@uigy/shared/components/ui-grid/models/ui-grid';
import { getAsset } from '@uigy/shared/constants/assetTypes.constant';
import { criticalityNames } from '@uigy/shared/constants/criticality-names.const';
import { RoutesStatesNamesEnum } from '@uigy/shared/enums/routesStatesNames.enum';
import { IndegyICellRendererParams } from '@uigy/shared/models/interfaces';
import { RouterRendererComponent } from '@uigy/shared/modules/grid/cell-renderers/router-renderer/router-renderer.component';
import { GridFormatterService } from '@uigy/shared/modules/grid/formatters/grid-formatter.service';
import { getFilterValues } from '@uigy/shared/modules/grid/models/grid';
import { LanguageService } from '@uigy/shared/services/language.service';
import { RISK_LEVELS, RISK_OFF_VALUE, RiskService } from '@uigy/shared/services/risk.service';
import Utils from '@uigy/utils';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class HighestRiskGridSettingsService {
  private languageService = inject(LanguageService);
  private inventoryDataHelperService = inject(InventoryDataHelperService);
  private riskService = inject(RiskService);
  private topRiskAssetsGQL = inject(TopRiskAssetsGQL);
  private gridFormatterService = inject(GridFormatterService);

  public readonly texts = this.languageService.texts.overview.highest_risk;
  public readonly buttonIcon: IconsEnum = IconsEnum.assets;
  public readonly buttonNavigateTo: string = `${RoutesStatesNamesEnum.inventory}?sort=${encodeURI(JSON.stringify(InventoryUiGridService.defaultSort))}`;
  public data: LeanAsset[] = [];

  constructor() {
  }

  public readonly NAME: ColDef = {
    headerName: this.texts.table_columns.name,
    field: AssetField.Name,
    valueGetter: (params) => params.data?.name,
    cellRenderer: RouterRendererComponent,
    cellRendererParams: {
      routerLink: [RoutesStatesNamesEnum.root, RoutesStatesNamesEnum.singleAssetPage],
      queryParams: (params: IndegyICellRendererParams<Asset>) => ({id: params.data.id}),
    },
    minWidth: 110,
    flex: 1
  };

  public readonly TYPE: ColDef = {
    headerName: this.texts.table_columns.type,
    field: AssetField.Type,
    cellRenderer: AssetTypeCellRendererComponent,
    tooltipValueGetter: (params) => this.gridFormatterService.assetTypeFormatter(params.value),
    valueFormatter: (params: ValueFormatterParams) => {
      if (params.node?.group) {
        return getAsset(params.value).name;
      }
      return params.value;
    },
    filterParams: {
      values: getFilterValues<GetIemAssetsQuery['assetsForGrid']['nodes'][number]>(AssetField.Type),
      valueFormatter: (params: ValueFormatterParams) => getAsset(params.value).name,
    },
    minWidth: 110,
    flex: 1
  };

  public readonly VENDOR: ColDef = {
    headerName: this.texts.table_columns.vendor,
    field: AssetField.Vendor,
    cellRenderer: VendorRendererComponent,
    filterParams: {
      values: getFilterValues<GetIemAssetsQuery['assetsForGrid']['nodes'][number]>(AssetField.Vendor),
    },
    minWidth: 110,
    flex: 1
  };

  public readonly RISK: ColDef = {
    headerName: this.texts.table_columns.risk_score,
    field: AssetField.Risk,
    cellRenderer: RiskScoreCellRendererComponent,
    sort: 'desc',
    valueGetter: (params) => this.inventoryDataHelperService.getRisk(params.data),
    keyCreator: params => this.riskService.getRiskModel(params.value).text.display,
    filterParams: {
      values: RISK_LEVELS.map(levelKey => this.languageService.texts.policies.policy_actions.event_severity.levels[levelKey].display),
      cellRenderer: RiskScoreComponent,
      suppressSorting: true,
      convertValuesToStrings: true,
    },
    tooltipValueGetter: (params: ITooltipParams): string =>
      +params.value === RISK_OFF_VALUE
        ? this.languageService.texts.general.risk.status.off
        : this.riskService.getRiskModel(params.value).text.display,
    minWidth: 200,
    flex: 1
  };

  private readonly CRITICALITY: ColDef = {
    headerName: this.texts.table_columns.criticality,
    field: AssetField.Criticality,
    cellRenderer: CriticalityCellRendererComponent,
    valueFormatter: (params: ValueFormatterParams): string => criticalityNames.get(params.value) || params.value,
    tooltipValueGetter: (params: ITooltipParams): string => params.valueFormatted ?? '',
    filterParams: {
      values: getFilterValues<GetIemAssetsQuery['assetsForGrid']['nodes'][number]>(AssetField.Criticality),
      valueFormatter: (params: ValueFormatterParams): string => criticalityNames.get(params.value) || params.value,
      comparator: (a: Criticality, b: Criticality) => {
        const arr = Array.from(criticalityNames.keys());
        return Utils.arrayComparator(arr)(a, b);
      },
    },
    enableRowGroup: false,
    minWidth: 110,
    flex: 1
  };

  public gridOptions: UiGridOptions<LeanAsset, undefined> = {
    gridId: UiGridIdsEnum.OVERVIEW_HIGHEST_RISK,
    gridTitle: this.texts.title,
    headerOptions: {
      hideHeader: true,
    },
    suppressColumnPinning: true,
    rowSelection: 'single',
    dataSource: this.topRiskAssetsGQL.fetch().pipe(
      map(res => res.data.assetsForGrid.nodes as LeanAsset[])
    ),
    columnDefs: [
      this.NAME,
      this.TYPE,
      this.VENDOR,
      this.RISK,
      this.CRITICALITY],
    autoSizeStrategy: {
      type: 'fitGridWidth',
      defaultMinWidth: 100,
    },
    disableSavingGridState: true,
    onGridSizeChanged: (params) => params.api.sizeColumnsToFit(),
  };


}
